import React, { useEffect, useRef, useState, useCallback } from 'react';
import GoogleMapsLoader from './GoogleMapsLoader';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onSelect: (address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    formatted: string;
    lat?: number;
    lng?: number;
    raw_place?: google.maps.places.PlaceResult;
  }) => void;
  placeholder?: string;
  className?: string;
  onManualSave?: (address: string, lat?: number, lng?: number) => void;
}

function AddressInput({
  value,
  onChange,
  onSelect,
  placeholder = "Enter an address",
  className = "",
  onManualSave,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  // Google appends one .pac-container per Autocomplete instance to <body> and
  // never removes it. We must position OUR container (not the first stale one
  // left behind by an earlier mount), and remove ours on unmount so they don't
  // accumulate across remounts (which broke the dropdown in edit modals).
  const pacContainerRef = useRef<HTMLElement | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const showDropdownRef = useRef(false);
  const lastSelectedPlaceRef = useRef<string | null>(null);
  const confirmedValueRef = useRef(value || '');
  const selectingRef = useRef(false);

  // Keep refs current so the place_changed listener always uses latest callbacks
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  const resolvePacContainer = useCallback((): HTMLElement | null => {
    if (pacContainerRef.current && document.body.contains(pacContainerRef.current)) {
      return pacContainerRef.current;
    }
    const all = Array.from(document.querySelectorAll<HTMLElement>('.pac-container'));
    if (all.length === 0) return null;
    // The visible container belongs to the currently active input; otherwise
    // the most recently created one is ours (instances append in mount order).
    return all.find((el) => el.style.display !== 'none' && el.offsetHeight > 0) || all[all.length - 1];
  }, []);

  const positionPacContainer = useCallback(() => {
    const pacContainer = resolvePacContainer();
    if (pacContainer && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      pacContainer.style.position = 'fixed';
      pacContainer.style.top = `${rect.bottom + 2}px`;
      pacContainer.style.left = `${rect.left}px`;
      pacContainer.style.width = `${rect.width}px`;
      pacContainer.style.zIndex = '100020';
      pacContainer.style.transform = 'none';
    }
  }, [resolvePacContainer]);

  // Initialize autocomplete once on mount, clean up only on unmount
  useEffect(() => {
    let cancelled = false;

    const processPlace = (place: google.maps.places.PlaceResult) => {
      if (!place?.address_components) {
        console.error('Google Maps Autocomplete: No address components in place:', place);
        return;
      }

      const components: { [key: string]: string } = {};
      place.address_components.forEach(component => {
        component.types.forEach(type => {
          components[type] = component.long_name;
          components[type + '_short'] = component.short_name;
        });
      });

      const streetNumber = components['street_number'] || '';
      const route = components['route'] || '';
      const city = components['locality'] || components['postal_town'] || '';
      const province = components['administrative_area_level_1_short'] || components['administrative_area_level_1'] || '';
      const postalCode = components['postal_code'] || '';
      const street = `${streetNumber} ${route}`.trim();

      const formattedParts = [street, city, province, postalCode, 'Canada'].filter(Boolean);
      const formattedAddress = formattedParts.join(', ');

      lastSelectedPlaceRef.current = formattedAddress;

      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();

      const addressData = {
        street,
        city,
        province,
        postalCode: postalCode || '',
        country: 'Canada',
        formatted: formattedAddress,
        lat,
        lng,
        raw_place: place
      };

      confirmedValueRef.current = formattedAddress;
      selectingRef.current = false;

      if (inputRef.current) {
        inputRef.current.value = formattedAddress;
      }

      onChangeRef.current(formattedAddress);
      onSelectRef.current(addressData);
    };

    const handlePlaceSelect = () => {
      if (!autocompleteRef.current) return;
      selectingRef.current = true;

      const place = autocompleteRef.current.getPlace();
      if (!place?.place_id) { selectingRef.current = false; return; }

      if (lastSelectedPlaceRef.current !== (place.formatted_address || '')) {
        processPlace(place);

        // Re-fetch to ensure postal code is included
        setTimeout(() => {
          if (cancelled) return;
          try {
            const service = new google.maps.places.AutocompleteService();
            const placesService = new google.maps.places.PlacesService(document.createElement('div'));
            service.getPlacePredictions(
              { input: place.formatted_address || '', componentRestrictions: { country: 'ca' } },
              (predictions, status) => {
                if (cancelled) return;
                if (status === google.maps.places.PlacesServiceStatus.OK && predictions?.[0]) {
                  placesService.getDetails(
                    { placeId: predictions[0].place_id, fields: ['address_components', 'formatted_address', 'geometry'] },
                    (detailedPlace, detailStatus) => {
                      if (cancelled) return;
                      if (detailStatus === google.maps.places.PlacesServiceStatus.OK && detailedPlace) {
                        processPlace(detailedPlace);
                      }
                    }
                  );
                }
              }
            );
          } catch { /* ignore */ }
        }, 100);
      }
    };

    const initializeAutocomplete = () => {
      if (!inputRef.current || !window.google?.maps?.places) return;

      try {
        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        }

        const options: google.maps.places.AutocompleteOptions = {
          componentRestrictions: { country: "ca" },
          fields: ["address_components", "formatted_address", "geometry", "place_id"],
          types: ["address"]
        };

        const containersBefore = document.querySelectorAll('.pac-container').length;

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          options
        );

        // Capture the container Google just created for THIS instance (it's the
        // newest .pac-container in the DOM). Deferred a tick since Google may
        // append it asynchronously.
        setTimeout(() => {
          if (cancelled) return;
          const all = document.querySelectorAll<HTMLElement>('.pac-container');
          if (all.length > containersBefore) {
            pacContainerRef.current = all[all.length - 1];
          }
        }, 0);

        autocompleteRef.current.addListener("place_changed", handlePlaceSelect);

        if (inputRef.current) {
          inputRef.current.addEventListener('focus', () => {
            showDropdownRef.current = true;
            setTimeout(() => positionPacContainer(), 100);
          });

          inputRef.current.addEventListener('blur', () => {
            setTimeout(() => {
              showDropdownRef.current = false;
              if (selectingRef.current) return;
              const currentInput = inputRef.current?.value || '';
              if (currentInput !== confirmedValueRef.current) {
                onChangeRef.current(confirmedValueRef.current);
                if (inputRef.current) {
                  inputRef.current.value = confirmedValueRef.current;
                }
              }
            }, 500);
          });

          inputRef.current.addEventListener('input', () => {
            positionPacContainer();
          });
        }

        setInitialized(true);
      } catch (error) {
        console.error('Google Maps Autocomplete: Error initializing:', error);
      }
    };

    const checkAndInitialize = () => {
      if (cancelled) return;
      if (window.google?.maps?.places) {
        initializeAutocomplete();
      } else {
        setTimeout(checkAndInitialize, 500);
      }
    };

    checkAndInitialize();

    // Scroll/resize handlers to keep pac-container positioned correctly
    const handleScroll = () => {
      if (showDropdownRef.current) positionPacContainer();
    };

    const containers = [
      document.querySelector('.create-mobile-booking-modal'),
      document.querySelector('.modal-content'),
      document.querySelector('[role="dialog"]'),
      document.querySelector('.overflow-y-auto'),
      window
    ];

    containers.forEach(c => {
      if (c) {
        c.addEventListener('scroll', handleScroll);
        c.addEventListener('resize', handleScroll);
      }
    });

    return () => {
      cancelled = true;
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      // Remove this instance's dropdown container — Google never cleans it up,
      // and orphaned containers hijack positioning for later mounts.
      if (pacContainerRef.current) {
        pacContainerRef.current.remove();
        pacContainerRef.current = null;
      }
      containers.forEach(c => {
        if (c) {
          c.removeEventListener('scroll', handleScroll);
          c.removeEventListener('resize', handleScroll);
        }
      });
    };
  }, []); // Only run once on mount

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (!newValue) {
      lastSelectedPlaceRef.current = null;
      confirmedValueRef.current = '';
    }
  };

  const handleManualSave = useCallback(async () => {
    const address = inputRef.current?.value || value;
    if (!address?.trim() || !onManualSave) return;
    setIsGeocoding(true);
    try {
      if (window.google?.maps) {
        const geocoder = new google.maps.Geocoder();
        const result = await new Promise<google.maps.GeocoderResult | null>((resolve) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
              resolve(results[0]);
            } else {
              resolve(null);
            }
          });
        });
        const lat = result?.geometry?.location?.lat();
        const lng = result?.geometry?.location?.lng();
        onManualSave(address, lat, lng);
      } else {
        onManualSave(address);
      }
    } catch {
      onManualSave(address);
    } finally {
      setIsGeocoding(false);
    }
  }, [value, onManualSave]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        style={{ paddingRight: onManualSave ? '36px' : undefined }}
      />
      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
        {onManualSave ? (
          <button
            type="button"
            onClick={handleManualSave}
            disabled={isGeocoding || !value?.trim()}
            className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
              isGeocoding || !value?.trim()
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-blue-500 hover:bg-blue-50 active:bg-blue-100 cursor-pointer'
            }`}
            title="Save address and geocode coordinates"
          >
            {isGeocoding ? (
              <div className="animate-spin rounded-full h-3.5 w-3.5 border border-blue-300 border-t-blue-600" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        ) : (
          <div className="text-gray-400" title={initialized ? "Google Maps Autocomplete Ready" : "Google Maps Not Loaded"}>
            {initialized ? '✓' : '⚠️'}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AddressAutocomplete(props: AddressAutocompleteProps) {
  // Read the API key from environment variables or use a default (for development only)
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8";
  
  return (
    <GoogleMapsLoader apiKey={apiKey}>
      <AddressInput {...props} />
    </GoogleMapsLoader>
  );
} 